// ⚠ CONTRAST, 2026-09-14. Real copy in this file was painted in
// `MK.smoke` (#59536a) and `MK.ash` (#8b8496) directly on `MK.obsidian`
// — about 2.7:1, well under the readable floor, on instructional text
// and shelf prose that people are meant to actually read.
// ⛑ `markData.js` already solved this: `ashP` / `smokeP` / `verdP`,
// with the note "THE NUMBERS ARE OUTPUTS, NOT TASTE". `Mark.js` uses
// them correctly and has zero raw `MK.ash`/`MK.smoke`; this file and
// the Marketplace were simply the two consumers the fix never reached.
// ⚑ Swapped ONLY where the token paints TEXT — borders and fills keep
// the originals, which is what the plated-vs-unplated note intends.
// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — PierMarketplace.js   ·   THE MARKETPLACE
// ────────────────────────────────────────────────────────────────────
// MYSTIC ALLY'S COUNTER. The Pier's vendor storefront — as distinct
// from the Boardwalk Bazaar, which is players selling to players. The
// Bazaar is a market. This is a SHOP, and it has a shopkeeper.
//
// Mystic Ally sells everything the Empire ever decides to sell. Today
// that is Mark plates. Tomorrow it is whatever caP'n names — and the
// shelves it lands on are already decided, forever, by THE THREE
// SHELVES (IE_CANON_PRIME §2.15). That is the whole reason this screen
// is built around shelves and not around categories: a new product
// costs a catalogue entry, not a design conversation.
//
// ── THE SHELVES ARE THE SCREEN ──────────────────────────────────────
// Each shelf gets its own BACKDROP, and the backdrop exemplifies the
// shelf the same way the Terror card animations do — flat and honest
// for Fable, elaborate and utterly still for Legendary, alive for
// Mythical. A player must be able to tell which shelf they are looking
// at with the labels covered up. If they cannot, the backdrop failed.
//
// ── WHAT THIS FILE MAY NOT DO ───────────────────────────────────────
//   • It does not write. Every write goes through markShop.js.
//   • It does not decide a price. Prices come from the catalogue.
//   • It does not decide whether a shelf is open. VENDING does.
//   • It does not build an exchange. Only the Crest Well exchanges.
// ════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from "react";
import { MK, mkCut, Kicker, Rule, injectMarkChrome, fmtNum } from "./markData";
import {
  SHELVES, SHELF_ORDER, MARK_VARIANTS, variantById,
  DEFAULT_VARIANT, auditShelves,
} from "./markVariants";
import { PlateFace, FACE_TEXT, ShelfChip, plateStyle, plateAura } from "./MarkFoil";
import {
  listenToOwnedVariants, ownsVariant, acquireVariant, equipVariant,
  stockFor, LOCK_REASON, priceOf, TESTING,
} from "./markShop";
// ⭐ ONE STORE (caP'n, 2026-09-25): the Minerium's shop IS this store.
import MineriumShelves from "./BonnieMinerium";
import { buyNameChange, nameChangePrice, NAME_MAX } from "./nameChange";

// ════════════════════════════════════════════════════════════════════
// KEYFRAMES — imperative, self-healing. Never a JSX <style> block.
// ════════════════════════════════════════════════════════════════════
const MP_KEYS_ID = "pier-marketplace-keys";
const MP_KEYS = `
@keyframes mpRise    { from { opacity:0; transform: translateY(14px) } to { opacity:1; transform:none } }
@keyframes mpFade    { from { opacity:0 } to { opacity:1 } }
@keyframes mpVeil    { 0%,100% { opacity:.30 } 50% { opacity:.62 } }
@keyframes mpDrift   { from { transform: translate3d(0,0,0) } to { transform: translate3d(-40px,-26px,0) } }
@keyframes mpTurn    { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
@keyframes mpBreathe { 0%,100% { opacity:.55; transform: scale(1) } 50% { opacity:1; transform: scale(1.06) } }
@keyframes mpType    { from { opacity:0 } to { opacity:1 } }
@keyframes mpToast   { 0% { opacity:0; transform: translateY(10px) } 12% { opacity:1; transform:none } 88% { opacity:1 } 100% { opacity:0 } }
`;
function ensureKeys() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(MP_KEYS_ID);
  if (el) { if (el.textContent === MP_KEYS) return; el.remove(); }
  const st = document.createElement("style");
  st.id = MP_KEYS_ID; st.textContent = MP_KEYS;
  document.head.appendChild(st);
}

// ════════════════════════════════════════════════════════════════════
// MYSTIC ALLY — the voice
// ────────────────────────────────────────────────────────────────────
// She is not a merchant with a mystical theme. She is a diviner who
// happens to keep a counter: she knows which plate you are going to
// take before you do, finds that mildly funny, and sells it to you
// anyway. She never upsells — she does not have to. And she will not
// take money she cannot verify, which is the till's real reason
// wearing a character's clothes.
// ════════════════════════════════════════════════════════════════════
const ALLY_GREETINGS = [
  ["You're here for a plate.", "Don't look surprised. I keep the counter — I also keep the reading."],
  ["Ah. The one with the hands.", "You'll want to see everything and take one thing. They always do."],
  ["I set out three shelves this morning.", "You'll walk past two of them. That's fine. Walk past them slowly."],
  ["A Mark is who you are.", "This is only what it's printed on. Which is not nothing, whatever W'ily says."],
  ["Take your time.", "The void's been out there four hundred years. It'll wait."],
];
const ALLY_SHELF_NOTES = {
  fable:
    "Fable. Flat foil, one colour — three if the plate earned it. There's no shame in it; " +
    "half the Empire wears Fable and the other half is lying.",
  legendary:
    "Legendary. The finest still holo anyone has ever pressed. Look at it as long as you like — " +
    "it will not do anything. That restraint IS the craft.",
  mythical:
    "Mythical. Lord-work. It is the Legendary beneath it, ascended — same plate, same design, " +
    "awake. Anything that moves and isn't somebody's Legendary risen is just a Legendary with " +
    "a fan on it, and I don't stock those.",
};
const ALLY_ON_BUY = [
  "Wear it somewhere I can see it.",
  "It suits you. I'd have said so either way, but it does.",
  "There. Now the plate knows your name too.",
];
const ALLY_TESTING = [
  "Everything's a Crest today. Don't get used to it.",
  "One Crest, anything on the boards. W'ily doesn't know yet.",
  "The prices are pinned to one. Take the expensive ones first — that's not advice, that's arithmetic.",
];
const ALLY_ON_LOCK = [
  "Not today.",
  "I can show you. I can't sell you.",
  "Ask me again when the counter's honest.",
];
const pick = (a, i) => a[Math.abs(i) % a.length];

// ════════════════════════════════════════════════════════════════════
// SHELF BACKDROPS — the exemplars
// ────────────────────────────────────────────────────────────────────
// One per shelf, and each one obeys its own shelf's law. This is the
// screen teaching the law without saying it: the Legendary backdrop is
// gorgeous and does not move, and standing next to the Mythical one it
// is obvious what animation buys.
// ════════════════════════════════════════════════════════════════════
function ShelfBackdrop({ shelf }) {
  const s = SHELVES[shelf];
  if (shelf === "fable") {
    // FLAT. Two colours, hard bands, no motion. Honest.
    return (
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
        background: `linear-gradient(103deg, ${s.tint}00 0%, ${s.tint}1c 34%, ${s.tint}00 46%, ${s.tintHi}14 68%, ${s.tintHi}00 82%)`,
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: `repeating-linear-gradient(103deg, ${s.tint}0e 0px, ${s.tint}0e 1px, transparent 1px, transparent 6px)`,
        }} />
      </div>
    );
  }
  if (shelf === "legendary") {
    // ELABORATE AND FROZEN. Every trick a still image allows: milled
    // sunburst, parked highlight, spectral bloom, print grain. Freeze
    // test passes by construction — there is nothing to freeze.
    return (
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{
          position: "absolute", left: "-40%", top: "-160%", width: "180%", height: "420%",
          background: `repeating-conic-gradient(from 12deg, ${s.tint}14 0deg, ${s.tint}14 2.6deg, transparent 2.6deg, transparent 6deg)`,
          opacity: 0.7,
        }} />
        <div style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(ellipse at 18% 0%, ${s.tintHi}26, transparent 62%), radial-gradient(ellipse at 86% 100%, ${s.tint}1e, transparent 58%)`,
        }} />
        <div style={{
          position: "absolute", top: "-20%", left: "44%", width: "16%", height: "150%",
          background: `linear-gradient(90deg, transparent, ${s.tintHi}30, transparent)`,
          transform: "skewX(-18deg)",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          background: `repeating-linear-gradient(112deg, #ffffff08 0px, #ffffff08 1px, transparent 1px, transparent 3px)`,
        }} />
      </div>
    );
  }
  // MYTHICAL — the same build, awake, plus what only motion allows.
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      <div style={{
        position: "absolute", left: "-40%", top: "-160%", width: "180%", height: "420%",
        background: `repeating-conic-gradient(from 12deg, ${s.tint}16 0deg, ${s.tint}16 2.6deg, transparent 2.6deg, transparent 6deg)`,
        opacity: 0.7, animation: "mpTurn 90s linear infinite",
      }} />
      <div style={{
        position: "absolute", inset: "-10%",
        background: `radial-gradient(ellipse at 18% 0%, ${s.tintHi}2e, transparent 62%), radial-gradient(ellipse at 86% 100%, ${s.tint}24, transparent 58%)`,
        animation: "mpVeil 7s ease-in-out infinite",
      }} />
      <div style={{
        position: "absolute", inset: "-12%",
        background: `radial-gradient(circle at 20% 30%, ${s.tintHi}1a 0 2px, transparent 2px), radial-gradient(circle at 70% 70%, ${s.tint}1a 0 2px, transparent 2px)`,
        backgroundSize: "90px 90px, 130px 130px",
        animation: "mpDrift 26s linear infinite alternate",
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: `repeating-linear-gradient(112deg, #ffffff08 0px, #ffffff08 1px, transparent 1px, transparent 3px)`,
      }} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE PLATE TILE — a real Mark plate, at shop size
// ────────────────────────────────────────────────────────────────────
// THE ONE FACE COROLLARY: this is not the shop's *impression* of a
// plate. It is the same slab style and the same MarkFoil the Mark card
// itself wears. A new variant appears here correctly with zero shop
// work, and drift between "in the shop" and "on your Mark" is
// unrepresentable rather than merely audited.
// ════════════════════════════════════════════════════════════════════
function PlateTile({ v, owned, worn, state, onBuy, onEquip, busy }) {
  const s = SHELVES[v.shelf];
  const locked = state !== "open";
  // ⚠ THE TAG READS THE TILL, not the catalogue. A sticker that says one
  // thing while the counter charges another is the specific way a shop
  // lies to a player, and it is one shared function away from impossible.
  const price = priceOf(v);
  const isFree = price === 0;

  return (
    <div style={{
      position: "relative", width: 232, flex: "0 0 auto",
      ...plateStyle(v),
      clipPath: mkCut(12),
      boxShadow: worn
        ? `0 0 0 1px ${s.tint}aa, 0 14px 44px #000b, 0 0 34px ${s.tint}33`
        : `0 14px 40px #000a`,
      animation: "mpRise .34s cubic-bezier(.22,.61,.36,1) both",
      overflow: "hidden",
    }}>
      {/* ⭐ THE FACE. Identical component, identical settings, to the
          one the Mark card mounts — that identity IS the no-catfish
          guarantee. Nothing here may be tuned "for the shop": if it
          looks different here than on the player, somebody has been
          sold a lie. (THE NO-CATFISH LAW, MarkFoil.js.) */}
      <PlateFace variant={v} style={{ height: 128 }}>
        <div style={{
          position: "absolute", inset: 0, display: "grid", placeItems: "center",
          textAlign: "center", padding: 12,
        }}>
          <div>
            <div style={{
              fontFamily: MK.display, fontSize: 21, color: MK.bone,
              lineHeight: 1.1, ...FACE_TEXT,
            }}>{v.name}</div>
            <div style={{ marginTop: 7 }}>
              {/* dark ink on the shelf's own colour — gold-on-gold was
                  unreadable, and a shelf label that cannot be read is a
                  shelf label that is not doing its job. */}
              <ShelfChip shelf={s} />
            </div>
          </div>
        </div>
      </PlateFace>
      {worn && (
        <div style={{
          position: "absolute", top: 8, right: 8, zIndex: 2,
          fontFamily: MK.mono, fontSize: 8, letterSpacing: ".22em",
          color: MK.void, background: s.tint, padding: "3px 6px",
        }}>WORN</div>
      )}

      <Rule tint={MK.edge} />

      {/* the plate's colour bleeding down onto the slab — a TINT, not a
          foil, so it can never take the blurb with it. Same helper the
          Mark card uses beneath its own face. */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", ...plateAura(v) }} />

      <div style={{ position: "relative", padding: "10px 12px 12px" }}>
        <div style={{
          fontFamily: MK.sans, fontSize: 12.5, lineHeight: 1.45,
          color: MK.ashP, minHeight: 68,
        }}>{v.blurb}</div>

        {v.ascendedFrom && (
          <div style={{ marginTop: 8 }}>
            <Kicker size={8} tint={SHELVES.mythical.tint}>
              ASCENDED FROM {variantById(v.ascendedFrom).name.toUpperCase()}
            </Kicker>
          </div>
        )}

        <div style={{
          marginTop: 10, display: "flex", alignItems: "center", gap: 8,
        }}>
          <div style={{
            fontFamily: MK.mono, fontSize: 12,
            color: isFree ? MK.verd : s.tintHi, flex: 1,
          }}>
            {owned ? "OWNED" : isFree ? "FREE" : `◆ ${fmtNum(price)}`}
            {!owned && TESTING && price !== (Number(v.priceDC) || 0) && (
              <span style={{ color: MK.smokeP, marginLeft: 6, fontSize: 10 }}>
                (◆{fmtNum(v.priceDC)})
              </span>
            )}
          </div>

          {owned ? (
            <button
              disabled={worn || busy}
              onClick={() => onEquip(v.id)}
              style={btn(worn ? MK.smokeP : s.tint, worn)}
            >{worn ? "WORN" : "WEAR"}</button>
          ) : locked ? (
            <button disabled title={LOCK_REASON} style={btn(MK.smoke, true)}>LOCKED</button>
          ) : (
            <button disabled={busy} onClick={() => onBuy(v.id)} style={btn(s.tint, busy)}>
              {isFree ? "TAKE" : "BUY"}
            </button>
          )}
        </div>

        {v.seal && (
          <div style={{
            marginTop: 9, fontFamily: MK.display, fontStyle: "italic",
            fontSize: 11.5, color: MK.smokeP, lineHeight: 1.4,
          }}>“{v.seal}”</div>
        )}
      </div>
    </div>
  );
}

const btn = (tint, dim) => ({
  fontFamily: MK.mono, fontSize: 10, letterSpacing: ".18em",
  padding: "6px 12px", cursor: dim ? "default" : "pointer",
  background: dim ? "transparent" : `${tint}1e`,
  color: dim ? MK.smokeP : tint,
  border: `1px solid ${dim ? MK.edge : tint + "77"}`,
  clipPath: mkCut(5),
  opacity: dim ? 0.6 : 1,
});

// ════════════════════════════════════════════════════════════════════
// THE MARKETPLACE
// ════════════════════════════════════════════════════════════════════
// ⭐⭐ BONNIE'S BUNNI BASKET — ONE STORE, TWO COUNTERS (caP'n, 2026-09-25)
// "the Minerium shop is just the Bonnie's Basket, just an easy way to access
// it, so combine both the stores." Bonnie took over Mystic Ally's wagon
// (PIERRE_PRIME L95), so the counter wears her name now.
//   • MARK PLATES — what this screen always sold (Crests, markShop.js)
//   • FOR YOUR MINERIUM — furnishings, growers, doors, skies, grounds, the
//     Hall's finishes and the PIER SKY (Dawgs, BonnieMinerium.jsx)
// The Minerium's button opens this same component on the second counter
// (`initialCounter="minerium"`) and passes `onHold`, so a bought piece still
// drops straight into the shed ghost there.
export default function PierMarketplace({ uid, playerName, mark, onClose, initialCounter = "marks", onHold = null }) {
  const [counter, setCounter] = useState(initialCounter === "minerium" ? "minerium" : "marks");
  const [owned, setOwned]   = useState({ [DEFAULT_VARIANT]: true });
  const [busy, setBusy]     = useState(false);
  const [toast, setToast]   = useState(null);
  const [tick]              = useState(() => Math.floor(Date.now() / 60000));
  const worn = mark?.variant || DEFAULT_VARIANT;

  useEffect(() => { ensureKeys(); injectMarkChrome(); }, []);
  useEffect(() => listenToOwnedVariants(uid, setOwned), [uid]);

  // The catalogue audits itself in development. A shelf violation is a
  // sentence in the console, not a mystery two months from now.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const bad = auditShelves();
    if (bad.length) console.warn("[THE THREE SHELVES] catalogue findings:\n • " + bad.join("\n • "));
    else console.log("[THE THREE SHELVES] catalogue is lawful.");
  }, []);

  const greeting = useMemo(() => pick(ALLY_GREETINGS, tick), [tick]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const say = (msg, good) => {
    setToast({ msg, good });
    setTimeout(() => setToast(null), 3600);
  };

  const onBuy = async (id) => {
    setBusy(true);
    const res = await acquireVariant(uid, id);
    setBusy(false);
    if (res.ok) {
      say(`${variantById(id).name} — ${pick(ALLY_ON_BUY, id.length)}`, true);
      // Taking a plate you have nothing else on wears it immediately.
      // Nobody buys a plate to leave it in a drawer.
      if (worn === DEFAULT_VARIANT) await equipVariant(uid, id);
    } else {
      say(res.reason === "till_closed" ? `${pick(ALLY_ON_LOCK, id.length)} ${res.message}` : res.message, false);
    }
  };

  const onEquip = async (id) => {
    setBusy(true);
    const res = await equipVariant(uid, id);
    setBusy(false);
    say(res.ok ? `Your Mark is ${variantById(id).name} now.` : res.message, res.ok);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9500,
      background: "rgba(3,3,6,.9)", backdropFilter: "blur(9px)",
      display: "flex", flexDirection: "column",
      animation: "mpFade .22s both", fontFamily: MK.sans,
    }}>
      {/* ── THE COUNTER ─────────────────────────────────────────── */}
      <div style={{
        position: "relative", borderBottom: `1px solid ${MK.edge}`,
        background: `linear-gradient(160deg, ${MK.slab}, ${MK.void})`,
        padding: "16px 22px 14px", flex: "0 0 auto",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <AllySigil />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Kicker size={9} tint={MK.violet}>W'ILY'S PIER · BONNIE'S WAGON</Kicker>
            <div style={{
              fontFamily: MK.display, fontSize: 27, color: MK.bone, marginTop: 2,
            }}>Bonnie's Bunni Basket</div>
            <div style={{
              fontFamily: MK.display, fontStyle: "italic", fontSize: 13.5,
              color: MK.ashP, marginTop: 5, lineHeight: 1.5, maxWidth: 720,
            }}>
              {greeting.map((l, i) => (
                <div key={i} style={{ animation: `mpType .5s ${i * 0.45}s both` }}>{l}</div>
              ))}
              {TESTING && (
                <div style={{ color: MK.verd, animation: `mpType .5s ${greeting.length * 0.45}s both` }}>
                  {pick(ALLY_TESTING, tick)}
                </div>
              )}
            </div>
          </div>
          <div
            onClick={onClose}
            title="Leave the counter (Esc)"
            style={{
              cursor: "pointer", fontFamily: MK.mono, fontSize: 12,
              color: MK.smokeP, padding: 6, flex: "0 0 auto",
            }}
          >✕ LEAVE</div>
        </div>
      </div>

      {/* ── THE TWO COUNTERS ────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, padding: "10px 22px", borderBottom: `1px solid ${MK.edge}`, flex: "0 0 auto", flexWrap: "wrap" }}>
        {[["marks", "MARK PLATES"], ["minerium", "FOR YOUR MINERIUM · SKIES"]].map(([id, label]) => (
          <button key={id} onClick={() => setCounter(id)} style={{
            cursor: "pointer", fontFamily: MK.mono, fontSize: 11, letterSpacing: ".16em",
            padding: "7px 14px", clipPath: mkCut(6),
            border: `1px solid ${counter === id ? MK.violet : MK.edge}`,
            background: counter === id ? `${MK.violet}33` : "transparent",
            color: counter === id ? MK.bone : MK.ashP,
          }}>{label}</button>
        ))}
      </div>

      {counter === "minerium" && (
        <div className="mk-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <MineriumShelves uid={uid} onHold={onHold ? (id) => { onClose?.(); onHold(id); } : null} />
        </div>
      )}

      {/* ── THE SHELVES ─────────────────────────────────────────── */}
      {counter === "marks" && (
      <div className="mk-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {SHELF_ORDER.map((shelfId) => {
          const stock = stockFor(shelfId);
          const s = SHELVES[shelfId];
          if (!stock.items.length) return null;
          return (
            <section key={shelfId} style={{
              position: "relative", borderBottom: `1px solid ${MK.edge}`,
              padding: "20px 22px 24px",
            }}>
              <ShelfBackdrop shelf={shelfId} />
              <div style={{ position: "relative" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                  <div style={{
                    fontFamily: MK.display, fontSize: 20, color: s.tintHi,
                    letterSpacing: ".06em",
                  }}>{s.glyph} {s.label}</div>
                  {stock.state !== "open" && (
                    <span style={{
                      fontFamily: MK.mono, fontSize: 8.5, letterSpacing: ".2em",
                      color: MK.smokeP, border: `1px solid ${MK.edge}`, padding: "3px 7px",
                    }}>TILL CLOSED</span>
                  )}
                  {TESTING && (
                    <span style={{
                      fontFamily: MK.mono, fontSize: 8.5, letterSpacing: ".2em",
                      color: MK.verd, border: `1px solid ${MK.verd}55`, padding: "3px 7px",
                    }}>◆1 · TESTING</span>
                  )}
                  <span style={{
                    fontFamily: MK.mono, fontSize: 10, color: MK.smokeP,
                    marginLeft: "auto",
                  }}>{s.test}</span>
                </div>

                <div style={{
                  fontFamily: MK.sans, fontSize: 13, color: MK.ashP,
                  marginTop: 5, maxWidth: 780, lineHeight: 1.5,
                }}>{s.rule}</div>
                <div style={{
                  fontFamily: MK.display, fontStyle: "italic", fontSize: 12.5,
                  color: MK.smokeP, marginTop: 6, maxWidth: 780, lineHeight: 1.5,
                }}>“{ALLY_SHELF_NOTES[shelfId]}”</div>

                <div className="mk-scroll" style={{
                  display: "flex", gap: 14, marginTop: 16,
                  overflowX: "auto", paddingBottom: 8,
                }}>
                  {stock.items.map((v) => (
                    <PlateTile
                      key={v.id}
                      v={v}
                      owned={ownsVariant(owned, v.id)}
                      worn={worn === v.id}
                      state={stock.state}
                      busy={busy}
                      onBuy={onBuy}
                      onEquip={onEquip}
                    />
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        {/* ── THE NAME CHANGE (caP'n 2026-10-20: "buy 1 'name change' item from the UP for 1 currency") ── */}
        <NameChangeCounter uid={uid} playerName={playerName} say={say} />

        {/* ── THE LAW, ON THE WALL ───────────────────────────────── */}
        <div style={{ padding: "18px 22px 42px", maxWidth: 900 }}>
          <Kicker tint={MK.smoke}>POSTED BEHIND THE COUNTER</Kicker>
          <div style={{
            marginTop: 8, fontFamily: MK.sans, fontSize: 12.5,
            color: MK.smokeP, lineHeight: 1.6,
          }}>
            Three shelves. Supply decides which shelf a thing sits on; the shelf
            decides what it looks like. <b style={{ color: SHELVES.fable.tint }}>Fable</b> is
            flat holo, one colour, three at the ceiling.
            {" "}<b style={{ color: SHELVES.legendary.tint }}>Legendary</b> is the best still
            holo there is and it does not move.
            {" "}<b style={{ color: SHELVES.mythical.tint }}>Mythical</b> is a Legendary
            ascended — the same plate, awake. Bonnie applies this to everything she
            will ever stock, and she does not take requests on the subject.
          </div>
          <div style={{
            marginTop: 10, fontFamily: MK.mono, fontSize: 10.5,
            color: MK.smokeP, opacity: .8,
          }}>
            IE_CANON_PRIME §2.15 · supply at §3.2 E · {Object.keys(MARK_VARIANTS).length} plates stocked
          </div>
        </div>
      </div>
      )}

      {TESTING && counter === "marks" && (
        <div style={{
          position: "absolute", right: 16, top: 96, zIndex: 2,
          fontFamily: MK.mono, fontSize: 9.5, letterSpacing: ".16em",
          color: MK.verd, border: `1px solid ${MK.verd}44`,
          background: "rgba(6,10,9,.7)", padding: "6px 10px",
        }}>EVERYTHING COSTS ◆1 WHILE TESTING</div>
      )}

      {toast && (
        <div style={{
          position: "absolute", left: "50%", bottom: 28, transform: "translateX(-50%)",
          background: MK.obsidian, border: `1px solid ${toast.good ? MK.verd : MK.edgeHot}`,
          clipPath: mkCut(8), padding: "10px 18px", maxWidth: "min(620px, 92vw)",
          fontFamily: MK.sans, fontSize: 13, color: toast.good ? MK.bone : MK.ashP,
          boxShadow: "0 18px 50px #000c", animation: "mpToast 3.6s both",
        }}>{toast.msg}</div>
      )}
    </div>
  );
}

// Her sigil. Not a portrait — the Pier has no vendor art yet, and a
// placeholder face is worse than a good mark. Three rings: what she
// keeps, what she reads, and the one she does not explain.
// One small counter, not a shelf: type the new name, pay, done. Your name is the Empire's
// (every game, every Mark), so this renames you everywhere at once. nameChange.js is the till.
function NameChangeCounter({ uid, playerName, say }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const price = nameChangePrice();
  const buy = async () => {
    if (busy) return;
    setBusy(true);
    const r = await buyNameChange(uid, draft);
    setBusy(false);
    if (r.ok) { setDraft(""); say(`There you go, ${r.name}. Suits you.`, true); }
    else say(r.message, false);
  };
  return (
    <div id="ie-name-change" style={{ padding: "18px 22px 0", maxWidth: 900 }}>
      <Kicker tint={MK.violet}>NAME CHANGE · ◆{price}</Kicker>
      <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontFamily: MK.sans, fontSize: 13, color: MK.smokeP }}>
          You're <b style={{ color: MK.bone }}>{playerName || "nameless"}</b> everywhere. New name?
        </span>
        <input value={draft} maxLength={NAME_MAX} placeholder="Your new name" aria-label="Your new name"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") buy(); }}
          onKeyUp={(e) => e.stopPropagation()}
          style={{ font: "inherit", fontSize: 13, padding: "6px 10px", minWidth: 180, background: MK.void, color: MK.bone, border: `1px solid ${MK.edge}`, borderRadius: 4 }} />
        <button onClick={buy} disabled={busy || !draft.trim() || !uid}
          style={{ font: "inherit", fontSize: 12, letterSpacing: ".08em", padding: "7px 14px", cursor: busy ? "wait" : "pointer",
                   background: MK.violet, color: MK.bone, border: "none", borderRadius: 4, opacity: busy || !draft.trim() ? 0.5 : 1 }}>
          {busy ? "…" : `CHANGE IT · ◆${price}`}
        </button>
      </div>
    </div>
  );
}

function AllySigil() {
  return (
    <div style={{
      position: "relative", width: 62, height: 62, flex: "0 0 auto",
      display: "grid", placeItems: "center",
    }}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        border: `1px solid ${MK.violet}55`, animation: "mpTurn 34s linear infinite",
        borderTopColor: MK.violet, borderRightColor: "transparent",
      }} />
      <div style={{
        position: "absolute", inset: 8, borderRadius: "50%",
        border: `1px solid ${MK.gold}44`, animation: "mpTurn 21s linear infinite reverse",
        borderBottomColor: MK.gold, borderLeftColor: "transparent",
      }} />
      <div style={{
        position: "absolute", inset: 17, borderRadius: "50%",
        background: `radial-gradient(circle at 40% 35%, ${MK.violet}55, transparent 70%)`,
        animation: "mpBreathe 5.5s ease-in-out infinite",
      }} />
      <div style={{
        fontFamily: MK.display, fontSize: 20, color: MK.goldHi,
        textShadow: `0 0 14px ${MK.violet}aa`,
      }}>✧</div>
    </div>
  );
}
